import React, { useState, useEffect, useCallback } from 'react';

// ---- Types ----

interface StaffManagementProps {
  eventTypes?: { id: number; title: string }[];
}

interface StaffMember {
  id: number;
  name: string;
  email: string;
  phone: string;
  title: string;
  bio: string;
  avatar_url: string | null;
  is_active: boolean;
  event_type_ids: number[];
  custom_availability: any;
  userId: number | null;
  inviteStatus: 'none' | 'pending' | 'accepted';
  invitedAt: string | null;
}

interface StaffFormData {
  name: string;
  email: string;
  phone: string;
  title: string;
  bio: string;
  avatarUrl: string;
  eventTypeIds: number[];
  isActive: boolean;
}

const EMPTY_FORM: StaffFormData = {
  name: '',
  email: '',
  phone: '',
  title: '',
  bio: '',
  avatarUrl: '',
  eventTypeIds: [],
  isActive: true,
};

// ---- API helpers ----

const API_BASE = '/backend/scheduling/staff';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const fetchStaffList = async (): Promise<StaffMember[]> => {
  const response = await fetch(`${API_BASE}/`, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error('Failed to load staff');
  const data = await response.json();
  return data.staff ?? data;
};

const createStaff = async (form: StaffFormData): Promise<StaffMember> => {
  const response = await fetch(`${API_BASE}/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      name: form.name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      title: form.title || undefined,
      bio: form.bio || undefined,
      avatarUrl: form.avatarUrl || undefined,
      eventTypeIds: form.eventTypeIds,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create staff member');
  }
  return response.json();
};

const updateStaff = async (staffId: number, form: Partial<StaffFormData> & { isActive?: boolean }): Promise<StaffMember> => {
  const body: Record<string, any> = {};
  if (form.name !== undefined) body.name = form.name;
  if (form.email !== undefined) body.email = form.email || undefined;
  if (form.phone !== undefined) body.phone = form.phone || undefined;
  if (form.title !== undefined) body.title = form.title || undefined;
  if (form.bio !== undefined) body.bio = form.bio || undefined;
  if (form.avatarUrl !== undefined) body.avatarUrl = form.avatarUrl || undefined;
  if (form.eventTypeIds !== undefined) body.eventTypeIds = form.eventTypeIds;
  if (form.isActive !== undefined) body.is_active = form.isActive;

  const response = await fetch(`${API_BASE}/${staffId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update staff member');
  }
  return response.json();
};

const deleteStaff = async (staffId: number): Promise<void> => {
  const response = await fetch(`${API_BASE}/${staffId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Cannot delete staff member');
  }
};

const inviteStaff = async (staffId: number): Promise<any> => {
  const response = await fetch(`${API_BASE}/${staffId}/invite`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to send invite');
  return data;
};

// ---- Helpers ----

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];

const getAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
};

// ---- Component ----

const StaffManagement: React.FC<StaffManagementProps> = ({ eventTypes = [] }) => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [form, setForm] = useState<StaffFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<StaffMember | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Invite state
  const [invitingId, setInvitingId] = useState<number | null>(null);

  const loadStaff = useCallback(async () => {
    try {
      setLoading(true);
      const list = await fetchStaffList();
      setStaff(list);
    } catch (err: any) {
      setError(err.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  // ---- Modal handlers ----

  const openAddModal = () => {
    setEditingStaff(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
    setError('');
  };

  const openEditModal = (member: StaffMember) => {
    setEditingStaff(member);
    setForm({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      title: member.title || '',
      bio: member.bio || '',
      avatarUrl: member.avatar_url || '',
      eventTypeIds: [...member.event_type_ids],
      isActive: member.is_active,
    });
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStaff(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingStaff) {
        await updateStaff(editingStaff.id, form);
        setSuccess(`${form.name} updated successfully`);
      } else {
        await createStaff(form);
        setSuccess(`${form.name} added successfully`);
      }
      closeModal();
      await loadStaff();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEventType = (eventTypeId: number) => {
    setForm(prev => {
      const ids = prev.eventTypeIds.includes(eventTypeId)
        ? prev.eventTypeIds.filter(id => id !== eventTypeId)
        : [...prev.eventTypeIds, eventTypeId];
      return { ...prev, eventTypeIds: ids };
    });
  };

  // ---- Active toggle (inline, no modal) ----

  const handleToggleActive = async (member: StaffMember) => {
    try {
      await updateStaff(member.id, { isActive: !member.is_active });
      setSuccess(`${member.name} ${member.is_active ? 'deactivated' : 'activated'}`);
      await loadStaff();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ---- Delete handlers ----

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setError('');

    try {
      await deleteStaff(deleteTarget.id);
      setSuccess(`${deleteTarget.name} removed`);
      setDeleteTarget(null);
      await loadStaff();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ---- Invite handler ----
  const handleInvite = async (member: StaffMember) => {
    setInvitingId(member.id);
    setError('');
    try {
      const result = await inviteStaff(member.id);
      setSuccess(result.message || 'Invite sent');
      await loadStaff();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setInvitingId(null);
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading staff members...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Staff Members</h2>
        <button
          onClick={openAddModal}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '0.95rem',
          }}
        >
          + Add Staff
        </button>
      </div>

      {/* Alerts */}
      {error && !showModal && !deleteTarget && (
        <div className="error-message" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button
            onClick={() => setError('')}
            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.25rem', padding: '0 4px' }}
          >
            x
          </button>
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          color: '#059669',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{success}</span>
          <button
            onClick={() => setSuccess('')}
            style={{ background: 'none', border: 'none', color: '#059669', cursor: 'pointer', fontSize: '1.25rem', padding: '0 4px' }}
          >
            x
          </button>
        </div>
      )}

      {/* Empty state */}
      {staff.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>
            <span role="img" aria-label="team">&#128101;</span>
          </div>
          <p style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: '500', color: '#374151' }}>
            No staff members yet
          </p>
          <p style={{ margin: '0 0 24px', color: '#6b7280', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
            Add your team to let clients book with specific providers.
          </p>
          <button
            onClick={openAddModal}
            style={{
              padding: '10px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            + Add Your First Staff Member
          </button>
        </div>
      )}

      {/* Staff grid */}
      {staff.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
          gap: '16px',
        }}>
          {staff.map(member => (
            <div
              key={member.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                padding: '20px',
                opacity: member.is_active ? 1 : 0.7,
              }}
            >
              {/* Card top row: avatar + info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                {/* Avatar */}
                {member.avatar_url ? (
                  <img
                    src={member.avatar_url}
                    alt={member.name}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: getAvatarColor(member.name),
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '1.1rem',
                    flexShrink: 0,
                  }}>
                    {getInitials(member.name)}
                  </div>
                )}

                {/* Name / title / email */}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.name}
                    </p>
                    {!member.is_active && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        flexShrink: 0,
                      }}>
                        Inactive
                      </span>
                    )}
                    {member.inviteStatus === 'accepted' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#f0fdf4',
                        color: '#059669',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        flexShrink: 0,
                      }}>
                        Login Active
                      </span>
                    )}
                    {member.inviteStatus === 'pending' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#fefce8',
                        color: '#ca8a04',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        flexShrink: 0,
                      }}>
                        Invite Sent
                      </span>
                    )}
                  </div>
                  {member.title && (
                    <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.title}
                    </p>
                  )}
                  {member.email && (
                    <p style={{ margin: '2px 0 0', color: '#9ca3af', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Event type badges */}
              {member.event_type_ids.length > 0 && eventTypes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                  {member.event_type_ids.map(etId => {
                    const et = eventTypes.find(e => e.id === etId);
                    if (!et) return null;
                    return (
                      <span
                        key={etId}
                        style={{
                          padding: '3px 10px',
                          backgroundColor: '#eff6ff',
                          color: '#3b82f6',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                        }}
                      >
                        {et.title}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Card actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
                {/* Active toggle */}
                <button
                  onClick={() => handleToggleActive(member)}
                  title={member.is_active ? 'Deactivate' : 'Activate'}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: member.is_active ? '#f0fdf4' : '#f3f4f6',
                    color: member.is_active ? '#059669' : '#6b7280',
                    border: `1px solid ${member.is_active ? '#bbf7d0' : '#d1d5db'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                  }}
                >
                  {member.is_active ? 'Active' : 'Inactive'}
                </button>

                {/* Invite button — show when staff has email and isn't already linked */}
                {member.email && member.inviteStatus !== 'accepted' && (
                  <button
                    onClick={() => handleInvite(member)}
                    disabled={invitingId === member.id}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: member.inviteStatus === 'pending' ? '#fefce8' : '#eff6ff',
                      color: member.inviteStatus === 'pending' ? '#ca8a04' : '#3b82f6',
                      border: '1px solid ' + (member.inviteStatus === 'pending' ? '#fde68a' : '#bfdbfe'),
                      borderRadius: '6px',
                      cursor: invitingId === member.id ? 'not-allowed' : 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                    }}
                  >
                    {invitingId === member.id ? 'Sending...' :
                     member.inviteStatus === 'pending' ? 'Resend Invite' : 'Invite Login'}
                  </button>
                )}

                <div style={{ flex: 1 }} />

                {/* Edit */}
                <button
                  onClick={() => openEditModal(member)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: '#374151',
                  }}
                >
                  Edit
                </button>

                {/* Delete */}
                <button
                  onClick={() => setDeleteTarget(member)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: '#dc2626',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Add / Edit Modal ---- */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 24px', fontSize: '1.25rem' }}>
              {editingStaff ? `Edit ${editingStaff.name}` : 'Add Staff Member'}
            </h3>

            {error && (
              <div className="error-message">{error}</div>
            )}

            {/* Name (required) */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '0.875rem' }}>
                Name <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '0.875rem' }}>
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="staff@example.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '0.875rem' }}>
                Phone
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="555-123-4567"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Title */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '0.875rem' }}>
                Title
              </label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Senior Stylist"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Bio */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '0.875rem' }}>
                Bio
              </label>
              <textarea
                value={form.bio}
                onChange={e => setForm({ ...form, bio: e.target.value })}
                placeholder="Brief description or experience..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Event types checklist */}
            {eventTypes.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.875rem' }}>
                  Services Offered
                </label>
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px',
                  maxHeight: '160px',
                  overflowY: 'auto',
                }}>
                  {eventTypes.map(et => (
                    <label
                      key={et.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: form.eventTypeIds.includes(et.id) ? '#eff6ff' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.eventTypeIds.includes(et.id)}
                        onChange={() => toggleEventType(et.id)}
                        style={{ accentColor: '#3b82f6' }}
                      />
                      <span style={{ fontSize: '0.9rem' }}>{et.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Active toggle (only show when editing) */}
            {editingStaff && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}>
                  <div
                    onClick={() => setForm({ ...form, isActive: !form.isActive })}
                    style={{
                      width: '44px',
                      height: '24px',
                      borderRadius: '12px',
                      backgroundColor: form.isActive ? '#3b82f6' : '#d1d5db',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      flexShrink: 0,
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      position: 'absolute',
                      top: '2px',
                      left: form.isActive ? '22px' : '2px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                  <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                </label>
              </div>
            )}

            {/* Modal actions */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                style={{
                  padding: '10px 24px',
                  backgroundColor: saving || !form.name.trim() ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving || !form.name.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                }}
              >
                {saving ? 'Saving...' : editingStaff ? 'Save Changes' : 'Add Staff Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete Confirmation Dialog ---- */}
      {deleteTarget && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setDeleteTarget(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: '1.15rem' }}>
              Delete {deleteTarget.name}?
            </h3>
            <p style={{ margin: '0 0 8px', color: '#6b7280', fontSize: '0.9rem' }}>
              This will permanently remove this staff member from your team.
            </p>
            <p style={{
              margin: '0 0 20px',
              padding: '10px 14px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '6px',
              color: '#92400e',
              fontSize: '0.85rem',
            }}>
              If this staff member has future bookings, deletion will fail. Reassign or cancel their bookings first.
            </p>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteTarget(null); setError(''); }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleting ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive style override for mobile */}
      <style>{`
        @media (max-width: 520px) {
          div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StaffManagement;
